class UserHelperService {

    constructor() {
        this.allRoles = null;
        this.allPermissions = null;
        this.roleIdToPermissionCodes = {};
        this.permissionCodeToPermissionId = {};

        this.customRoleCode = 'CUSTOM';
        this.customRole = null;

        this.viewAllClientsCode = 'VIEW_ALL_CLIENTS';
        this.viewPTClientsCode = 'VIEW_PT_CLIENTS';
        this.clientPermissions = [this.viewAllClientsCode, this.viewPTClientsCode];
    }

    /***
     * Takes in a User object and a permission reference code and returns whether the user has the given permission
     * by following a few steps:
     *   1) If there is no User logged in, then we immediately return false
     *   2) If the User's role is Custom, we check their custom permissions for the given code
     *   3) Otherwise, fetch this User's role and the permissions that correspond to that role to see if any match
     *      the code
     *
     * @param user                  a User object
     * @param permissionCode        the reference code name for a permission to check ("USER_ADMIN",
     *                              "SURVEY_ADMIN', etc.)
     * @param forceCustom           a boolean. If true, the user's custom_permissions will be checked regardless of
     *                              what the user's assigned role is. Otherwise, the user's custom_permissions will
     *                              only be checked if the user has the Custom role.
     *
     * @returns {boolean}
     */
    hasPermission(user, permissionCode, forceCustom=false) {
        // If there is no User or if the user is deactivated or holding, immediately return false
        if (!user) {
            return false;
        }

        const permissionId = this.permissionCodeToPermissionId[permissionCode];

        // If the user's role is the Custom Role, check their custom permissions
        if (forceCustom || (this.customRole && user.user_role_id === this.customRole.id)) {
            return user.custom_permission_ids.indexOf(permissionId) > -1;
        }
        // Otherwise check the permissions attached to their assigned role
        const permissionCodes = this.roleIdToPermissionCodes[user.user_role_id];
        if (!permissionCodes) {
            return false;
        }
        return permissionCodes.indexOf(permissionCode) > -1;
    }

    /***
     * Takes in a list of roles received from the API and stores them within this service for later access.
     *
     * Also does two things:
     *   1) Populates the roleIdToPermissionCodes object, which allows us to make the ID of a role to a list of the
     *      code values for each permission assigned to that role.
     *   2) stores a reference to the Custom role so that we are able to determine whether to check a user's role or
     *      their custom permissions in the hasPermission() function.
     *
     * @param roles     a list of UserRole objects returned from the API
     */
    initializeRoles(roles) {
        this.allRoles = roles;
        for (let role of this.allRoles) {
            this.roleIdToPermissionCodes[role.id] = role.permissions.map((r) => r.code);
            if (role.code === this.customRoleCode) {
                this.customRole = role;
            }
        }
    }

    /***
     * Takes in a list of permission received from the API and stores them within this service for later access.
     *
     * Also populates the permissionCodeToPermissionId object, which is used by hasPermission() to quickly fetch the
     * ID of a permission by passing in its reference code.
     *
     * @param permissions       a list of UserPermission objects returned from the API
     */
    initializePermissions(permissions) {
        this.allPermissions = permissions;
        for (let permission of this.allPermissions) {
            this.permissionCodeToPermissionId[permission.code] = permission.id;
        }
    }

    /***
     * Takes in the reference code for a permission and returns the corresponding permission object.
     *
     * @param permissionCode        the reference code for the permission being fetched
     *
     * @returns {UserPermission object}
     */
    getPermissionByCode(permissionCode) {
        if (!this.allPermissions) {
            return null;
        }
        const permissions = this.allPermissions.filter((p) => p.code === permissionCode);
        if (permissions.length === 0) {
            return null;
        }
        return permissions[0];
    }

    /***
     * Takes in an object whose keys are reference codes for user permissions and whose values are booleans determining
     * whether that permission should be included in the return value. Returns an array of the IDs of the remaining
     * permissions.
     *
     * @param conditionalPermissions        an object mapping reference codes to booleans
     *
     * @returns {Array of ints}
     */
    getConditionalPermissionIds(conditionalPermissions) {
        const includedPermissionIds = [];
        for (let permissionCode in conditionalPermissions) {
            const includePermission = conditionalPermissions[permissionCode];
            if (includePermission) {
                const permission = this.getPermissionByCode(permissionCode);
                includedPermissionIds.push(permission.id)
            }
        }
        return includedPermissionIds;
    }

    /***
     * Returns a filtered down list of UserPermissions to only include client-access permission that the provided
     * user currently has.
     *
     * @param user      a User object
     *
     * @returns {Array of UserPermission objects}
     */
    getClientPermissions(user) {
        return this.allPermissions.filter((p) => {
            return this.clientPermissions.indexOf(p.code) > -1 && user.custom_permission_ids.indexOf(p.id) > -1;
        });
    }

    /***
     * Returns a filtered down list of UserPermissions to only include custom permissions (i.e. permissions that do not
     * have to do with client access) that the user currently has.
     *
     * @param user      a User object
     *
     * @returns {Array of UserPermission objects}
     */
    getCustomPermissions(user) {
        return this.allPermissions.filter((p) => {
            return this.clientPermissions.indexOf(p.code) === -1 && user.custom_permission_ids.indexOf(p.id) > -1;
        });
    }

}

export default UserHelperService;


// WEBPACK FOOTER //
// ./app/common/user-helper/user-helper-service.js